/* @typescript-eslint/no-unused-vars */
import {
  DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined
} from '@ant-design/icons';
import {
  Button, message, Modal, Space, Switch, Tooltip, Typography
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppSelector } from 'app/store';
import HeaderPage from 'common/components/HeaderPage';
import PageTable from 'common/components/PageTable';
import {
  FilterDataProps, FilterValueProps,
  mappingFilterFields, mappingParamsFilter,
  mappingQueryParamsFilter
} from 'common/components/PageTable/filter';
import {
  deleteAddOnPackageByIdsService, getAllAddOnsPackageService,
  updateAddOnPackageIsCheckoutService,
  updateAddOnPackageStatusService
} from 'common/services/packages';
import { DisplayPackageTypes } from 'common/services/packages/types';
import { PACKAGE_DURATION_LABELS, ROUTE_PATHS } from 'common/utils/constant';
import { formatDateTime } from 'common/utils/functions';

const AddOnPackage = () => {
  /* Hook */
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /* Selectors */
  const { defaultPageSize } = useAppSelector((state) => state.system);
  const { system: { advancedFilter } } = useAppSelector((state) => state);

  /* Search Params */
  const [searchParams, setSearchParams] = useSearchParams();

  /* States */
  const [currentPage, setCurrentPage] = useState(1);
  const [currentView, setCurrentView] = useState(defaultPageSize);

  const params = useMemo(() => {
    const paramsObj = { ...Object.fromEntries(searchParams.entries()) };
    if (searchParams.has('page')) delete paramsObj.page;
    return paramsObj;
  }, [searchParams]);

  const [keyword, setKeyword] = useState('');
  const [selectedFilterList, setSelectedFilterList] = useState<
    FilterValueProps[]>(mappingQueryParamsFilter(params));

  /* Variables */
  const returnParamsFilter = useMemo(
    () => mappingParamsFilter(selectedFilterList),
    [selectedFilterList]
  );

  const filterFields: FilterDataProps[] = useMemo(
    () => mappingFilterFields('servicePackageAddons', advancedFilter),
    [advancedFilter]
  );

  /* React-query */
  const { data: packageData, isLoading: isPackageLoading } = useQuery(
    ['addOnPackageData', currentPage, currentView, keyword, selectedFilterList],
    () => getAllAddOnsPackageService({
      params: {
        page: currentPage,
        limit: currentView,
        keyword,
        ...returnParamsFilter,
      },
    }),
  );

  const { mutate: deletePackageMutate } = useMutation(
    ['deletePackage'],
    (ids: number[]) => deleteAddOnPackageByIdsService({ ids }),
    {
      onSuccess: () => {
        message.success(t('message.deleteSuccess'));
        navigate(ROUTE_PATHS.PACKAGES_MANAGEMENT);
        queryClient.invalidateQueries(['packageData', currentPage, currentView]);
      },
      onError: () => {
        message.error(t('message.deleteError'));
      },
    }
  );

  const { mutate: updateStatusMutate, isLoading: updateStatusLoading } = useMutation(
    'updateFaqStatus',
    async (data: {
      id: number, statusId: number
    }) => updateAddOnPackageStatusService(data.id, data.statusId),
    {
      onSuccess: () => {
        message.success(t('message.updateStatusSuccess'));
        queryClient.invalidateQueries(['packageData', currentPage, currentView]);
      },
      onError: () => {
        message.error(t('message.updateStatusError'));
      }
    }
  );

  const { mutate: updateIsCheckoutMutate, isLoading: updateIsCheckoutLoading } = useMutation(
    'updateIsCheckoutAddOnsPackage',
    async (data: {
      id: number, isCheckout: boolean
    }) => updateAddOnPackageIsCheckoutService(data.id, data.isCheckout),
    {
      onSuccess: () => {
        message.success(t('message.updateStatusSuccess'));
        queryClient.invalidateQueries(['packageData', currentPage, currentView]);
      },
      onError: () => {
        message.error(t('message.updateStatusError'));
      }
    }
  );

  /* Functions */
  const handleSetCurrentPage = (page: number) => {
    setCurrentPage(page);
    setSearchParams({ page: page.toString() }, { replace: true });
  };

  const handleSetCurrentView = (view: number) => {
    setCurrentView(view);
  };

  const handleFilter = (data: FilterValueProps) => {
    const typeFilter = String(data.filter).split('.')[1];
    if ((typeFilter === 'isNull' || typeFilter === 'isNotNull') && selectedFilterList.find((item) => item.key === data.key)) {
      return;
    }
    const counter = selectedFilterList.filter(
      (item) => item.field === data.field && item.filter === data.filter
    ).length;
    setSelectedFilterList([...selectedFilterList, { ...data, index: counter }]);
  };

  const handleDeleteFilter = (key: string, index?: number) => {
    const tempList = selectedFilterList.slice();
    setSelectedFilterList(tempList.filter((item) => !(item.key === key && item.index === index)));
  };

  /* Datas */
  const columns: ColumnsType<any> = useMemo(
    () => [
      {
        title: t('system.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: {
          compare: (a: any, b: any) => a.name.localeCompare(b.name),
        },
        sortDirections: ['descend', 'ascend'],
        render: (_name: string, data) => (
          <Typography.Text>
            {data.name}
          </Typography.Text>
        ),
      },
      {
        title: t('system.status'),
        dataIndex: 'status',
        key: 'status',
        sorter: {
          compare: (a: any, b: any) => a.name.localeCompare(b.name),
        },
        sortDirections: ['descend', 'ascend'],
        render: (_name: string, data) => (
          <Switch
            checked={data.status === 2}
            onChange={(checked) => updateStatusMutate({
              id: data.id,
              statusId: checked ? 2 : 1,
            })}
          />
        ),
      },
      {
        title: t('package.duration'),
        dataIndex: 'duration',
        key: 'duration',
        render: (_name: string, data: { duration: number }) => (
          <Typography.Text>
            {t(PACKAGE_DURATION_LABELS[data.duration as keyof typeof PACKAGE_DURATION_LABELS])}
          </Typography.Text>
        ),
      },
      {
        title: t('system.createdAt'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        sorter: {
          compare: (a: any, b: any) => a.name.localeCompare(b.name),
        },
        sortDirections: ['descend', 'ascend'],
        render: (_name: string, data) => (
          <Typography.Text>
            {formatDateTime(data.createdAt || '')}
          </Typography.Text>
        ),
      },
      {
        title: t('system.updatedAt'),
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        sorter: {
          compare: (a: any, b: any) => a.name.localeCompare(b.name),
        },
        sortDirections: ['descend', 'ascend'],
        render: (_name: string, data) => (
          <Typography.Text>
            {formatDateTime(data.updatedAt || '')}
          </Typography.Text>
        ),
      },
      {
        title: t('package.isCheckout'),
        dataIndex: 'isCheckout',
        key: 'isCheckout',
        render: (_name: string, data) => (
          <Switch
            checked={data.isCheckout}
            onChange={() => updateIsCheckoutMutate({
              id: data.id,
              isCheckout: !data.isCheckout
            })}
          />
        ),
      },
      {
        title: t('system.action'),
        key: 'action',
        width: 100,
        align: 'center',
        render: (_name: string, data) => (
          <Space>
            <Tooltip title="View">
              <Button
                icon={<EyeOutlined />}
                onClick={() => navigate(`${ROUTE_PATHS.ADD_ON_PACKAGE_DETAIL_VIEW}?id=${data.id}`)}
              />
            </Tooltip>
            <Tooltip title="Edit">
              <Button
                icon={<EditOutlined />}
                onClick={() => navigate(`${ROUTE_PATHS.ADD_ON_PACKAGE_DETAIL}?id=${data.id}`)}
              />
            </Tooltip>
            <Tooltip title="Delete">
              <Button
                icon={<DeleteOutlined />}
                onClick={() => {
                  Modal.confirm({
                    className: 't-pagetable_deleteRecordModal',
                    autoFocusButton: 'cancel',
                    okText: t('system.ok'),
                    cancelText: t('system.cancel'),
                    cancelButtonProps: {
                      type: 'primary',
                    },
                    okButtonProps: {
                      type: 'default',
                    },
                    title: t('message.confirmDeleteRecord'),
                    onOk: () => {
                      deletePackageMutate([data.id]);
                    },
                  });
                }}
              />
            </Tooltip>
          </Space>
        ),
      },
    ],
    []
  );

  const tableData: DisplayPackageTypes[] = useMemo(
    () => packageData?.data.map((item) => ({
      id: item.servicePackageData.id,
      name: item.servicePackageData.name,
      duration: item.servicePackageData.duration,
      status: item.servicePackageData.status,
      isCheckout: item.servicePackageData.isCheckout,
      createdAt: item.servicePackageData.createdAt,
      updatedAt: item.servicePackageData.updatedAt,
    })) || [],
    [packageData]
  );

  return (
    <>
      <HeaderPage
        fixed
        title={t('sidebar.add_on_package')}
        rightHeader={(
          <Space>
            <Button
              type="primary"
              onClick={() => {
                navigate(`${ROUTE_PATHS.ADD_ON_PACKAGE_DETAIL}`);
              }}
            >
              <PlusOutlined />
              {t('system.create')}
            </Button>
          </Space>
        )}
      />
      <div className="t-mainlayout_wrapper">
        <PageTable
          isLoading={isPackageLoading || updateStatusLoading || updateIsCheckoutLoading}
          handleSearch={setKeyword}
          tableProps={{
            initShowColumns: ['name', 'status', 'duration', 'isCheckout', 'createdAt', 'updatedAt', 'action'],
            columns,
            pageData: tableData,
            currentPage,
            pageSize: currentView,
            handleSetCurrentPage,
            handleSetCurrentView,
            total: packageData?.meta.total || 1,
            noBaseCol: true,
            noDeleteLanguage: true,
            filterFields
          }}
          filtersDataTable={{
            handleFilter,
            selectedFilterList,
            handleDeleteFilter
          }}
        />
      </div>
    </>
  );
};

export default AddOnPackage;
